import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { CheckCircle, AlertCircle } from 'lucide-react';

const QuickDiscountCreator = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const createQuickDiscount = async () => {
    setLoading(true);
    setSuccess(null);
    setError(null);

    try {
      const today = new Date().toISOString().split('T')[0];
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + 30);
      const validTill = futureDate.toISOString().split('T')[0];

      const { data, error: insertError } = await supabase
        .from('discounts')
        .insert([
          {
            title: 'Quick Test Discount - Active Now',
            reason: 'Testing discount and GST system',
            discount_percentage: 10.00,
            gst_percentage: 18.00,
            valid_from: today,
            valid_till: validTill,
            applies_to: 'ALL',
            is_active: true
          }
        ])
        .select();

      if (insertError) throw insertError;

      setSuccess({
        message: 'Discount created successfully!',
        data: data[0]
      });
    } catch (err) {
      setError(err.message);
      console.error('Error creating discount:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Quick Discount Creator</h1>
          <p className="text-gray-600 mb-8">Create a test discount that's active immediately</p>

          {/* Discount Details */}
          <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">This will create:</h2>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-purple-600 mr-2">✓</span>
                <span><strong>Title:</strong> Quick Test Discount - Active Now</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-2">✓</span>
                <span><strong>Discount:</strong> 10% off</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-2">✓</span>
                <span><strong>GST:</strong> 18%</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-2">✓</span>
                <span><strong>Valid From:</strong> Today</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-2">✓</span>
                <span><strong>Valid Till:</strong> 30 days from today</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-2">✓</span>
                <span><strong>Applies To:</strong> ALL (Wellness + Consultation)</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-2">✓</span>
                <span><strong>Status:</strong> Active</span>
              </li>
            </ul>
          </div>

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                <div className="flex-1">
                  <h3 className="text-green-800 font-semibold mb-2">{success.message}</h3>
                  <div className="bg-white rounded p-3 text-sm">
                    <p className="text-gray-700"><strong>ID:</strong> {success.data.id}</p>
                    <p className="text-gray-700"><strong>Title:</strong> {success.data.title}</p>
                    <p className="text-gray-700"><strong>Valid:</strong> {success.data.valid_from} to {success.data.valid_till}</p>
                  </div>
                  <div className="mt-4 space-y-2">
                    <a
                      href="/discount-management"
                      className="inline-block bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors mr-2"
                    >
                      View in Admin Panel
                    </a>
                    <a
                      href="/test-pricing"
                      className="inline-block bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors mr-2"
                    >
                      Test Pricing
                    </a>
                    <a
                      href="/booking"
                      className="inline-block bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Try Booking
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <AlertCircle className="text-red-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <h3 className="text-red-800 font-semibold mb-1">Error Creating Discount</h3>
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Create Button */}
          <button
            onClick={createQuickDiscount}
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 rounded-lg font-semibold text-lg hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
          >
            {loading ? 'Creating...' : 'Create Quick Discount'}
          </button>

          {/* Navigation Links */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600 mb-3">Quick Links:</p>
            <div className="flex flex-wrap gap-2">
              <a href="/discount-management" className="text-sm text-purple-600 hover:text-purple-700 underline">
                Discount Management
              </a>
              <span className="text-gray-400">•</span>
              <a href="/test-pricing" className="text-sm text-purple-600 hover:text-purple-700 underline">
                Test Pricing
              </a>
              <span className="text-gray-400">•</span>
              <a href="/booking" className="text-sm text-purple-600 hover:text-purple-700 underline">
                Booking Page
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickDiscountCreator;

